import React, { useState, useEffect, useRef } from 'react';
import { Loader2, ExternalLink, HardDrive } from 'lucide-react';
import { getDriveStatus, getDriveAuthUrl, exportToDrive } from '../lib/api';
import { toast } from 'sonner';
import { DemoChip } from './ui/demo-chip';

export default function GoogleDriveExport({ draftId, draftTitle }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    const check = async () => {
      try {
        const status = await getDriveStatus();
        setConnected(status.connected);
      } catch {
        // Not connected
      } finally {
        setLoading(false);
      }
    };
    check();
  }, []);

  // The OAuth popup is opened with noopener — it relays its result here via
  // BroadcastChannel (see OAuthPopupRelay in App.js)
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('draftflow-oauth');
    channel.onmessage = (e) => {
      if (e.data === 'drive_connected') {
        setConnected(true);
        toast.success('Google Drive connected!');
      } else if (typeof e.data === 'string' && e.data.startsWith('drive_error:')) {
        toast.error(`Google Drive connection failed: ${e.data.slice('drive_error:'.length)}`);
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleConnect = async () => {
    try {
      const { url } = await getDriveAuthUrl();
      window.open(url, '_blank', 'width=500,height=700,noopener,noreferrer');

      // Fallback: poll connection status while the user completes consent
      clearInterval(pollRef.current);
      let tries = 0;
      pollRef.current = setInterval(async () => {
        tries += 1;
        if (tries > 40) { clearInterval(pollRef.current); return; }
        try {
          const status = await getDriveStatus();
          if (status.connected) {
            clearInterval(pollRef.current);
            setConnected(true);
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch {
      toast.error('Failed to start Google Drive connection');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setExportResult(null);
    try {
      const result = await exportToDrive(draftId);
      setExportResult(result);
      toast.success('Exported to Google Drive!');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Google Drive session expired. Please reconnect.');
        setConnected(false);
      } else {
        toast.error('Export failed. Try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  if (loading) return null;

  if (!connected) {
    return (
      <DemoChip label="Sign in to export to Drive">
        <button onClick={handleConnect}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-teal-flow/20 text-teal-flow hover:bg-teal-flow/30 rounded-lg text-xs font-medium transition-colors"
          title="Connect Google Drive">
          <HardDrive size={14} />
          Connect Drive
        </button>
      </DemoChip>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <DemoChip label="Sign in to export to Drive">
        <button onClick={handleExport} disabled={exporting}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-teal-flow/20 text-teal-flow hover:bg-teal-flow/30 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          title="Export to Google Drive">
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <HardDrive size={14} />}
          {exporting ? 'Exporting...' : 'Save to Drive'}
        </button>
      </DemoChip>
      {exportResult && (
        <a href={exportResult.file_url} target="_blank" rel="noopener noreferrer"
          className="text-teal-flow text-xs hover:underline flex items-center gap-1">
          Open <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
}
